'use client'

import { getApiErrorMessage, useApi } from '@repo/api'
import { useDotEnv } from '@repo/dot-env/client'
import type { GetCustomerSetupFormToken } from '@repo/schema/customer-setup-form'
import { useMutation } from '@repo/tanstack/query'
import { useState } from 'react'

import { buildCustomerSetupLink } from '@/app/@protected/_utils/customer-setup-links'

const CREATE_ERROR_MESSAGE = 'Could not create the set-up form link. Try again.'

export type CustomerSetupLinkCopyResult =
  | { status: 'copied'; url: string }
  | { status: 'manual-copy'; url: string }
  | { status: 'unavailable' }

export type CustomerSetupLinkCreateAndCopyResult =
  | {
      copyResult: CustomerSetupLinkCopyResult
      status: 'created'
      token: GetCustomerSetupFormToken
    }
  | { message: string; status: 'error' }
  | { status: 'pending' | 'unavailable' }

export type CustomerSetupLinkCreationResult =
  | { message: string; status: 'error' }
  | {
      status: 'created'
      token: GetCustomerSetupFormToken
      url: string
    }
  | { status: 'pending' | 'unavailable' }

// Lives outside the hook because the React Compiler can't lower try/finally yet.
const createTokenWhilePending = async <T>({
  create,
  onCreated,
  setPending,
}: {
  create: () => Promise<GetCustomerSetupFormToken>
  onCreated: (_token: GetCustomerSetupFormToken) => Promise<T> | T
  setPending: (_pending: boolean) => void
}): Promise<T | { message: string; status: 'error' }> => {
  setPending(true)

  try {
    const token = await create()
    return await onCreated(token)
  } catch (error) {
    const message = getApiErrorMessage(error, CREATE_ERROR_MESSAGE)[0]?.message

    return { message: message ?? CREATE_ERROR_MESSAGE, status: 'error' }
  } finally {
    setPending(false)
  }
}

export const useCustomerSetupLinkActions = ({ companyId }: { companyId?: number } = {}) => {
  const api = useApi()
  const env = useDotEnv()
  const [isPending, setIsPending] = useState(false)
  const [manualCopyUrl, setManualCopyUrl] = useState<string>()
  const { mutateAsync } = useMutation(
    api.companies.customer.setupForm.tokens.create.mutationOptions({
      meta: { invalidates: [] },
    }),
  )

  const buildLinkFromToken = (token: string) => {
    if (!env.CUSTOMER_SETUP_FORM_URL) return undefined

    try {
      return buildCustomerSetupLink(env.CUSTOMER_SETUP_FORM_URL, token)
    } catch {
      return undefined
    }
  }

  const copyLink = async (url: string): Promise<CustomerSetupLinkCopyResult> => {
    try {
      await navigator.clipboard.writeText(url)
      return { status: 'copied', url }
    } catch {
      setManualCopyUrl(url)
      return { status: 'manual-copy', url }
    }
  }

  const copyToken = async (token: string): Promise<CustomerSetupLinkCopyResult> => {
    const url = buildLinkFromToken(token)

    if (typeof url !== 'string') return { status: 'unavailable' }

    return copyLink(url)
  }

  const createWithToken = async <T>(
    onCreated: (_token: GetCustomerSetupFormToken) => Promise<T> | T,
  ): Promise<T | { message: string; status: 'error' } | { status: 'pending' | 'unavailable' }> => {
    if (!env.CUSTOMER_SETUP_FORM_URL) return { status: 'unavailable' }
    if (isPending) return { status: 'pending' }

    const input = companyId ? { company_id: companyId } : {}

    return createTokenWhilePending({
      create: () => mutateAsync(input),
      onCreated,
      setPending: setIsPending,
    })
  }

  const createAndCopy = (): Promise<CustomerSetupLinkCreateAndCopyResult> =>
    createWithToken(async token => {
      const copyResult = await copyToken(token.token)

      return { copyResult, status: 'created', token }
    })

  const createLink = (): Promise<CustomerSetupLinkCreationResult> =>
    createWithToken(token => {
      const url = buildLinkFromToken(token.token)

      if (typeof url !== 'string') return { status: 'unavailable' }

      return { status: 'created', token, url }
    })

  return {
    closeManualCopy: () => setManualCopyUrl(undefined),
    copyToken,
    createAndCopy,
    createLink,
    isPending,
    manualCopyUrl,
  }
}
